import { useId, useState } from 'react';
import { Button } from './ui.js';

/**
 * A person on a shared email address (admin@, info@, office@), in every Review.
 *
 * A shared address is a warning, not a rejection: a real person may use it as their
 * business email. It is never treated as one person automatically, so it starts left out,
 * and a reviewer either skips the person or confirms — explicitly, for this person — that
 * the address is theirs. Only then is Keap checked for that person, and nothing is sent
 * until the reviewer approves as usual. Shown only for a shared address; an ordinary
 * contact never sees any of this.
 */

export interface SharedAddressState {
  /** A reviewer has confirmed the address belongs to this person, and it still holds. */
  readonly confirmed: boolean;
  /** What Keap holds for the confirmed person: null when it could not be checked. */
  readonly match: 'new' | 'existing' | 'in_use' | null;
  /** True when the Keap contact was chosen by a person rather than matched by name. */
  readonly chosen?: boolean;
  /** For `in_use`: the Keap contacts using the address, by name. */
  readonly candidates: ReadonlyArray<{ readonly externalContactId: string; readonly name: string | null }>;
}

export function SharedAddressConfirm({ name, email, state, editable, busy, onConfirm, onSkip, onChoose, idBase }: {
  name: string;
  email: string;
  state: SharedAddressState;
  editable: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onSkip: () => void;
  onChoose: (externalContactId: string) => void;
  idBase?: string;
}) {
  const [asking, setAsking] = useState(false);
  const [sure, setSure] = useState(false);
  const auto = useId();
  const id = `${idBase ?? auto}-shared`;
  const who = name.trim() || 'this person';
  const btn = 'inline-flex min-h-11 items-center rounded-md px-3 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900';

  if (!state.confirmed) {
    return (
      <div className="mt-2 min-w-0 rounded-md bg-amber-50 p-3 text-sm text-amber-900" data-shared-address="unconfirmed" role="group" aria-labelledby={`${id}-note`}>
        <p id={`${id}-note`} className="[overflow-wrap:anywhere]">This email may be shared by more than one person.</p>
        {!editable ? null : !asking ? (
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={onSkip} className={`${btn} border border-amber-300 bg-white text-amber-900`}>
              Skip this person
            </button>
            <button
              type="button" disabled={busy || !name.trim()} onClick={() => { setAsking(true); setSure(false); }}
              className={`${btn} bg-slate-900 text-white disabled:opacity-50`}
            >
              Add this person
            </button>
            {!name.trim() ? <p className="w-full text-xs">Add the person’s name first.</p> : null}
          </div>
        ) : (
          <div className="mt-2 min-w-0 space-y-2" data-shared-confirm="">
            <p className="font-medium [overflow-wrap:anywhere]">
              This email address may be used by several people. Are you sure it belongs to {who}?
            </p>
            <label className="flex min-h-11 items-start gap-3" htmlFor={`${id}-sure`}>
              <input
                id={`${id}-sure`} type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-amber-400"
              />
              <span className="[overflow-wrap:anywhere]">
                I confirm this is the correct person and want to use this email address ({email}) for their contact.
              </span>
            </label>
            <div className="flex flex-wrap gap-2">
              <Button disabled={!sure || busy} onClick={() => { setAsking(false); onConfirm(); }}>Continue</Button>
              <Button variant="secondary" disabled={busy} onClick={() => setAsking(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 min-w-0 rounded-md bg-slate-50 p-3 text-sm text-slate-800" data-shared-address={state.match ?? 'unchecked'}>
      <p className="[overflow-wrap:anywhere]">You confirmed {who} uses this email.</p>
      {state.match === 'new' ? (
        <p className="mt-1 text-slate-600">No Keap contact uses this email yet, so a new contact will be created for {who}.</p>
      ) : state.match === 'existing' ? (
        <p className="mt-1 text-slate-600">
          {state.chosen ? 'Matched to the Keap contact you chose.' : `Matched to the Keap contact named ${who}.`} It is kept as it is in Keap.
        </p>
      ) : state.match === 'in_use' ? (
        <fieldset className="mt-1 min-w-0" disabled={!editable || busy}>
          <legend className="text-amber-900">
            {state.candidates.length > 1
              ? 'More than one Keap contact uses this email. Choose the correct person.'
              : 'Keap already has a contact with this email under another name. Choose them if they are this person.'}
          </legend>
          <ul className="mt-2 space-y-2">
            {state.candidates.map((c) => (
              <li key={c.externalContactId} className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded border border-slate-200 bg-white px-3 py-1.5">
                <span className="min-w-0 [overflow-wrap:anywhere]">{c.name ?? 'Unnamed contact'}</span>
                <button
                  type="button" onClick={() => onChoose(c.externalContactId)}
                  className={`${btn} border border-slate-300 bg-white text-slate-800`}
                  aria-label={`${c.name ?? 'Unnamed contact'} is ${who}`}
                >
                  This is {who}
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-slate-600">
            If none of them is {who}, skip them. ContactPilot won’t create a second contact with the same email.
          </p>
        </fieldset>
      ) : (
        <p className="mt-1 text-amber-900">Keap couldn’t be checked for this email. Confirm again in a moment.</p>
      )}
      {editable ? (
        <button type="button" disabled={busy} onClick={onSkip} className={`${btn} mt-1 px-0 text-slate-700 underline`}>
          Skip this person
        </button>
      ) : null}
    </div>
  );
}

/** A shared-address participant as the email Review stores it, as the component needs it. */
export function sharedStateOf(p: {
  isRoleMailbox: boolean;
  sharedAddressConfirmation?: { keapContactId?: string | null } | null;
  crm: { kind: string; matchedBy?: string; candidates?: ReadonlyArray<{ externalContactId: string; name: string | null }> };
}): SharedAddressState {
  if (!p.sharedAddressConfirmation) return { confirmed: false, match: null, candidates: [] };
  const k = p.crm.kind;
  return {
    confirmed: true,
    match: k === 'new' ? 'new' : k === 'existing' ? 'existing' : k === 'ambiguous' ? 'in_use' : null,
    chosen: k === 'existing' && p.crm.matchedBy === 'human_choice',
    candidates: k === 'ambiguous' ? (p.crm.candidates ?? []) : [],
  };
}
